import { isAbortError } from '@app/errors/AbortError'

import { sleep } from '@app/utils/sleep'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import { ComputedValue } from '@app/packages/reactives/ComputedValue'
import { ReactiveValue } from '@app/packages/reactives/ReactiveValue'
import type { TrackableValue } from '@app/packages/reactives/TrackableValue'

import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import { asError } from './shared'

export type PromiseState = {
  id: string
  hash: string
  abort: () => void
  promise: (check?: (id: string, hash: string) => ThunkAction<unknown>) => Promise<void>
  current: TrackableValue<Promise<void> | null>
  loading: TrackableValue<boolean>
  aborted: TrackableValue<boolean>
  resolved: TrackableValue<boolean>
  error: TrackableValue<Error | null>
  clear: () => void
}

export const deletePromiseState = (id: string) =>
  createThunk(dispatch => {
    const context = dispatch(getPromiseContext())
    const state = dispatch(getPromiseState(id))
    state?.abort()
    delete context[id]
  })

export const createPromise = (id: string, hashes: string | string[], action: ThunkAction<Promise<void>>) =>
  createThunk(dispatch => {
    const context = dispatch(getPromiseContext())
    const current = dispatch(getPromiseState(id))
    const hash = Array.isArray(hashes) ? hashes.join(':') : hashes

    if (current && hash === current.hash) {
      return current
    }
    if (current) {
      dispatch(deletePromiseState(id))
    }

    const abortController = new AbortController()
    const currentReactive = new ReactiveValue<Promise<void> | null>(null)
    const abortedReactive = new ReactiveValue<boolean>(false)
    const errorReactive = new ReactiveValue<Error | null>(null)
    const resolvedReactive = new ReactiveValue<boolean>(false)
    const loadingReactive = new ComputedValue([currentReactive], current => !!current)
    const promiseFactory = async (check?: (id: string, hash: string) => ThunkAction<void>) => {
      if (currentReactive.value) return currentReactive.value
      const cp = Promise.withResolvers<void>()
      currentReactive.update(() => cp.promise)
      abortedReactive.update(() => false)
      errorReactive.update(() => null)
      sleep(10, abortController.signal)
        .then(() => dispatch(withAbortSignal(abortController.signal, action)))
        .then(() => (check ? dispatch(check(id, hash)) : undefined))
        .catch(e => {
          if (isAbortError(e)) {
            abortedReactive.update(() => true)
          } else {
            errorReactive.update(() => asError(e))
          }
        })
        .finally(() => {
          cp.resolve()
          resolvedReactive.update(() => true)
          currentReactive.update(() => null)
        })
      return cp.promise
    }
    const pstate: PromiseState = {
      id,
      abort: () => {
        abortController.abort()
        const state = context[id]
        if (state === pstate) delete context[id]
      },
      hash,
      promise: promiseFactory,
      current: currentReactive,
      loading: loadingReactive,
      resolved: resolvedReactive,
      aborted: abortedReactive,
      error: errorReactive,
      clear: () => dispatch(deletePromiseState(id)),
    }
    context[id] = pstate
    return pstate
  })

const PROMISES_KEY = Symbol('PROMISES_KEY')

const getPromiseContext = () =>
  createThunk((_dispatch, _getState, context) => {
    return context.getRootContext().getOrInsert<Record<string, PromiseState>>(PROMISES_KEY, () => ({}))
  })

const getPromiseState = (id: string) =>
  createThunk(dispatch => {
    const context = dispatch(getPromiseContext())
    const val = context[id]
    if (!val) return null
    return val
  })
